import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectAdminToken } from "../../admin/sessionSlice";
import "./stage4.css";
import {
  STAGE_ROW_KEY,
  clearStageActive,
  getRedirectForPlayedStage,
  hydrateCompletedStagesFromRecord,
  markStageCompleted,
  normalizePlayerName,
  saveGameStageAndReport,
  setStageActive,
} from "../../functions/stageReportSync";

const STAGE_NUM = 4;
const GRID_SIZE = 3;
const PIECE_COUNT = GRID_SIZE * GRID_SIZE;
/** old/stage4.php: points += 5 once the puzzle is fully assembled. */
const POINTS_ON_COMPLETE = 5;
/** old/rules.php assigns a random reference image (1-9.jpg) once per attempt. */
const JIGSAW_IMAGE_KEY = "stage4_jigsaw_img";

const STAGE4_BG = `${process.env.PUBLIC_URL}/assets/game/stage4-bg.jpg`;
const STAGE4_BG_MOBILE = `${process.env.PUBLIC_URL}/assets/game/stage4_mob.png`;
const CURSOR_URL = `${process.env.PUBLIC_URL}/assets/game/cursor.png`;

const MOBILE_PORTRAIT_MQ = "(max-width: 768px) and (orientation: portrait)";

function jigsawImageUrl(n) {
  return `${process.env.PUBLIC_URL}/assets/game/jigsaw/${n}.jpg`;
}

function pickJigsawImage() {
  let stored = sessionStorage.getItem(JIGSAW_IMAGE_KEY);
  if (!stored) {
    stored = String(1 + Math.floor(Math.random() * 9));
    sessionStorage.setItem(JIGSAW_IMAGE_KEY, stored);
  }
  return stored;
}

/** Fisher-Yates, re-rolled until it's a full derangement (no piece starts in
 * its correct slot) — matches old's "expert" difficulty forcing a reshuffle
 * whenever correctTiles() finds any tile already in place after shuffling. */
function shuffledOrder(count) {
  const order = Array.from({ length: count }, (_, i) => i);
  const hasAnyCorrectSlot = (arr) => arr.some((v, i) => v === i);
  do {
    for (let i = order.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [order[i], order[j]] = [order[j], order[i]];
    }
  } while (hasAnyCorrectSlot(order));
  return order;
}

function parseClockToSeconds(clock) {
  const parts = String(clock ?? "00:00")
    .trim()
    .split(":")
    .map((p) => parseInt(p, 10));
  if (parts.some((n) => !Number.isFinite(n))) return 0;
  if (parts.length >= 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length >= 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
}

function formatSecondsToClock(totalSec) {
  const sec = Math.max(0, Math.floor(totalSec));
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${String(min).padStart(2, "0")}:${String(rem).padStart(2, "0")}`;
}

function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

export default function Stage4() {
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);

  const [stageRowId, setStageRowId] = useState(
    () => sessionStorage.getItem(STAGE_ROW_KEY) || null
  );
  const [dbReady, setDbReady] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [stage4Points, setStage4Points] = useState(0);
  const [displayTotalScore, setDisplayTotalScore] = useState(0);
  const [order, setOrder] = useState(() => shuffledOrder(PIECE_COUNT));
  const orderRef = useRef(order);
  const [draggingSlot, setDraggingSlot] = useState(null);
  const [solved, setSolved] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const referenceImage = useMemo(() => jigsawImageUrl(pickJigsawImage()), []);

  const stage4PointsRef = useRef(0);
  const solvedRef = useRef(false);
  const elapsedRef = useRef(0);
  const baseElapsedRef = useRef(0);
  const sessionStartRef = useRef(null);
  const otherStagesTotalRef = useRef(0);
  const baselinePointsRef = useRef(0);
  const submittedRef = useRef(false);
  const timerStartedRef = useRef(false);
  const dragSlotRef = useRef(null);
  /** DOM node of the tile currently being dragged, so it can be repositioned directly. */
  const draggedElRef = useRef(null);
  const dragStartPointRef = useRef({ x: 0, y: 0 });
  const activePointerIdRef = useRef(null);

  const storedUser = useMemo(() => getStoredUser(), []);
  const isDemoBypass = useMemo(() => {
    const role = String(storedUser.role || "").toLowerCase();
    const source = String(storedUser.source || "").toUpperCase();
    const sessionId = String(storedUser.sessionId || "");
    return (
      sessionId === "demobypass" ||
      role === "demobypass" ||
      source === "DEMO" ||
      sessionId.startsWith("demo")
    );
  }, [storedUser]);
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      backgroundImage: body.style.backgroundImage,
      backgroundSize: body.style.backgroundSize,
      backgroundRepeat: body.style.backgroundRepeat,
      backgroundPosition: body.style.backgroundPosition,
      backgroundAttachment: body.style.backgroundAttachment,
      cursor: body.style.cursor,
    };
    const applyBackground = () => {
      const isMobilePortrait = window.matchMedia(MOBILE_PORTRAIT_MQ).matches;
      body.style.backgroundImage = `linear-gradient(rgba(0, 0, 0, .4), rgba(0, 0, 0, .4)), url("${
        isMobilePortrait ? STAGE4_BG_MOBILE : STAGE4_BG
      }")`;
      body.style.backgroundSize = isMobilePortrait ? "cover" : "100% 100%";
      body.style.backgroundRepeat = "no-repeat";
      body.style.backgroundPosition = "center center";
      body.style.backgroundAttachment = "fixed";

      // Desktop's grid+reference fit one viewport, so a hard lock is fine —
      // but stacked mobile-portrait (reference image above the full 3x3 grid)
      // is taller than one screen, and an inline overflow:hidden (which always
      // wins over CSS) would trap the rest of the puzzle off-screen.
      if (isMobilePortrait) {
        html.style.overflow = "";
        body.style.overflow = "auto";
      } else {
        html.style.overflow = "hidden";
        body.style.overflow = "hidden";
      }
    };

    html.classList.add("stage4-lock");
    body.classList.remove("common-bg");
    body.classList.add("stage4-jigsaw-page");
    applyBackground();
    body.style.cursor = `url("${CURSOR_URL}"), auto`;
    window.addEventListener("resize", applyBackground);
    window.addEventListener("orientationchange", applyBackground);
    return () => {
      html.classList.remove("stage4-lock");
      body.classList.remove("stage4-jigsaw-page");
      window.removeEventListener("resize", applyBackground);
      window.removeEventListener("orientationchange", applyBackground);
      html.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
      body.style.backgroundImage = prev.backgroundImage;
      body.style.backgroundSize = prev.backgroundSize;
      body.style.backgroundRepeat = prev.backgroundRepeat;
      body.style.backgroundPosition = prev.backgroundPosition;
      body.style.backgroundAttachment = prev.backgroundAttachment;
      body.style.cursor = prev.cursor;
    };
  }, []);

  useEffect(() => {
    stage4PointsRef.current = stage4Points;
    setDisplayTotalScore(otherStagesTotalRef.current + stage4Points);
  }, [stage4Points]);

  useEffect(() => {
    if (!dbReady || timerStartedRef.current) return undefined;
    timerStartedRef.current = true;
    sessionStartRef.current = Date.now();
    const tick = setInterval(() => {
      const sessionSec = Math.floor(
        (Date.now() - sessionStartRef.current) / 1000
      );
      const elapsed = baseElapsedRef.current + sessionSec;
      elapsedRef.current = elapsed;
      setElapsedSec(elapsed);
    }, 1000);
    return () => clearInterval(tick);
  }, [dbReady]);

  useEffect(() => {
    let cancelled = false;

    async function loadStageFromDb() {
      const uid = storedUser.userId || storedUser.userid || storedUser.id;
      const sessionId = storedUser.sessionId;
      const organizationId = storedUser.organizationId;

      if (isDemoBypass || !backendBase || !adminToken) {
        if (!cancelled) setDbReady(true);
        return;
      }
      if (!uid || !sessionId || !organizationId) {
        if (!cancelled) setDbReady(true);
        return;
      }

      try {
        let rowId = sessionStorage.getItem(STAGE_ROW_KEY);
        if (!rowId) {
          const startRes = await fetch(`${backendBase}/welcomeStageStart`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${adminToken}`,
            },
            body: JSON.stringify({
              userId: uid,
              sessionId,
              organizationId,
              email: storedUser.email || "",
              emp_id: storedUser.employeeId || storedUser.emp_id || "",
              name: normalizePlayerName(storedUser),
              user_type: storedUser.user_type || storedUser.userType || "",
            }),
          });
          const startData = await startRes.json().catch(() => ({}));
          if (startRes.ok && startData.success && startData.id != null) {
            rowId = String(startData.id);
            sessionStorage.setItem(STAGE_ROW_KEY, rowId);
          }
        }

        if (!rowId) {
          if (!cancelled) setDbReady(true);
          return;
        }
        if (!cancelled) setStageRowId(rowId);

        const stageRes = await fetch(`${backendBase}/getGameStage`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: uid,
            sessionId,
            organizationId,
            stageId: rowId,
          }),
        });
        const stageData = await stageRes.json().catch(() => ({}));
        if (cancelled) return;

        if (stageRes.ok && stageData.success && stageData.stage) {
          const st = stageData.stage;
          hydrateCompletedStagesFromRecord(st);
          const resumeRoute = getRedirectForPlayedStage(st, STAGE_NUM);
          if (resumeRoute) {
            navigate(resumeRoute, { replace: true });
            return;
          }
          const dbTimeSec = parseClockToSeconds(st.time);
          const dbTotal = Number(st.total_score) || 0;
          const dbStage4 = Number(st.stage4) || 0;
          baseElapsedRef.current = dbTimeSec;
          elapsedRef.current = dbTimeSec;
          setElapsedSec(dbTimeSec);
          otherStagesTotalRef.current = Math.max(0, dbTotal - dbStage4);
          baselinePointsRef.current = dbStage4;
          stage4PointsRef.current = dbStage4;
          setStage4Points(dbStage4);
          setDisplayTotalScore(dbTotal);
        }

        setStageActive(STAGE_NUM, storedUser);
        setDbReady(true);
      } catch (err) {
        console.error("Stage4 loadStageFromDb:", err);
        if (!cancelled) setDbReady(true);
      }
    }

    loadStageFromDb();
    return () => {
      cancelled = true;
    };
  }, [backendBase, adminToken, isDemoBypass, storedUser, navigate]);

  const finishStage = useCallback(
    async (skip = false) => {
      if (submitting || submittedRef.current) return;
      submittedRef.current = true;
      setSubmitting(true);

      const timeStr = formatSecondsToClock(elapsedRef.current);
      // old/stage4.php's send_skip() submits `initalPoints` (points already saved
      // before this attempt); a completed puzzle submits the +5 it just earned.
      const stage4Score = skip ? baselinePointsRef.current : stage4PointsRef.current;
      const stageId = stageRowId || sessionStorage.getItem(STAGE_ROW_KEY);
      const finalTotal = otherStagesTotalRef.current + stage4Score;

      // old/stage4.php hands off to puzzle-img.php next, not the final "gameover"
      // screen — that now happens in Stage5, so this just records progress like
      // stages 1-3 do (saveGameStageAndReport already mirrors it to the external
      // report internally).
      if (!isDemoBypass && backendBase && adminToken && stageId) {
        const saveResult = await saveGameStageAndReport({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
          stageId,
          stageNumber: STAGE_NUM,
          score: stage4Score,
          time: timeStr,
          total_score: finalTotal,
        });
        if (saveResult.total_score != null) {
          setDisplayTotalScore(saveResult.total_score);
        }
      }

      clearStageActive(STAGE_NUM);
      markStageCompleted(STAGE_NUM, storedUser);

      if (isDemoBypass) {
        window.location.href =
          String(process.env.REACT_APP_BASE_URL || "").trim() || "/";
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 1500));
      navigate("/stage5");
    },
    [submitting, storedUser, isDemoBypass, backendBase, adminToken, stageRowId, navigate]
  );

  const handleSkip = useCallback(() => {
    finishStage(true);
  }, [finishStage]);

  // old/css/puzzle.css: a tile whose data-inplace="true" gets pointer-events:none —
  // once a piece lands in its correct slot it's locked and can't be moved again.
  const isSlotLocked = useCallback((slot) => orderRef.current[slot] === slot, []);

  const swapSlots = useCallback(
    (a, b) => {
      if (
        a === b ||
        solvedRef.current ||
        submittedRef.current ||
        isSlotLocked(a) ||
        isSlotLocked(b)
      ) {
        return;
      }
      const next = [...orderRef.current];
      [next[a], next[b]] = [next[b], next[a]];
      orderRef.current = next;
      setOrder(next);

      const isSolved = next.every((pieceId, slot) => pieceId === slot);
      if (isSolved) {
        solvedRef.current = true;
        setSolved(true);
        setStage4Points((p) => p + POINTS_ON_COMPLETE);
        setTimeout(() => finishStage(false), 1200);
      }
    },
    [isSlotLocked, finishStage]
  );

  const resetDraggedTile = useCallback(() => {
    const el = draggedElRef.current;
    if (el) {
      el.style.transitionProperty = "";
      el.style.pointerEvents = "";
      el.style.transform = "";
      el.style.zIndex = "";
    }
    dragSlotRef.current = null;
    draggedElRef.current = null;
    activePointerIdRef.current = null;
    setDraggingSlot(null);
  }, []);

  // Pointer Events (unify mouse/touch/pen in one code path) drive the whole
  // interaction: setPointerCapture guarantees this element keeps receiving
  // move/up events for the rest of the gesture no matter where the pointer
  // travels, so there's no dependence on the browser's own drag-and-drop hit
  // testing (which is what kept breaking — see old/css/puzzle.css for the
  // grayscale/lock behavior this still has to preserve).
  const handlePointerDown = useCallback(
    (slot, e) => {
      if (solvedRef.current || submittedRef.current || isSlotLocked(slot)) return;
      e.preventDefault();
      const el = e.currentTarget;
      el.setPointerCapture(e.pointerId);

      dragSlotRef.current = slot;
      draggedElRef.current = el;
      activePointerIdRef.current = e.pointerId;
      dragStartPointRef.current = { x: e.clientX, y: e.clientY };
      setDraggingSlot(slot);

      // Suppress only the transform transition while actively following the
      // pointer (want instant 1:1 movement) — leave the grayscale-reveal
      // transition alone so it still animates normally once dropped.
      el.style.transitionProperty = "filter";
      el.style.zIndex = "10";
      // So elementFromPoint (on release) can see the tile underneath instead
      // of finding this tile itself, wherever it's currently sitting on top.
      el.style.pointerEvents = "none";
    },
    [isSlotLocked]
  );

  const handlePointerMove = useCallback((e) => {
    if (e.pointerId !== activePointerIdRef.current) return;
    const el = draggedElRef.current;
    if (!el) return;
    const { x, y } = dragStartPointRef.current;
    el.style.transform = `translate(${e.clientX - x}px, ${e.clientY - y}px)`;
  }, []);

  const handlePointerUp = useCallback(
    (e) => {
      if (e.pointerId !== activePointerIdRef.current) return;
      const from = dragSlotRef.current;
      const el = draggedElRef.current;
      if (el?.hasPointerCapture?.(e.pointerId)) {
        el.releasePointerCapture(e.pointerId);
      }

      if (from != null) {
        const targetEl = document
          .elementFromPoint(e.clientX, e.clientY)
          ?.closest("[data-slot]");
        const to = targetEl ? Number(targetEl.dataset.slot) : null;
        if (to != null && !Number.isNaN(to)) {
          swapSlots(from, to);
        }
      }

      resetDraggedTile();
    },
    [swapSlots, resetDraggedTile]
  );

  const handlePointerCancel = useCallback(
    (e) => {
      if (e.pointerId !== activePointerIdRef.current) return;
      resetDraggedTile();
    },
    [resetDraggedTile]
  );

  if (!dbReady) {
    return <div className="stage4-loading">Loading game…</div>;
  }

  const clock = formatSecondsToClock(elapsedSec);
  const pieceBgPos = (pieceId) => {
    const row = Math.floor(pieceId / GRID_SIZE);
    const col = pieceId % GRID_SIZE;
    const step = 100 / (GRID_SIZE - 1);
    return `${col * step}% ${row * step}%`;
  };

  return (
    <div className="stage4-page">
      <div className="stage4-hud-row">
        <div className="stage4-hud-col stage4-hud-col--left">
          <div className="stage4-timericon" aria-label={`Time ${clock}`}>
            Time: {clock}
          </div>
        </div>
        <div className="stage4-hud-col stage4-hud-col--center">
          <div className="stage4-heading-box">
            <h1 className="stage4-head-title">
              Complete the jigsaw puzzle to make your final contribution. With each
              correct move, the pieces will change color, revealing the company's core
              values on the screen.
            </h1>
          </div>
        </div>
        <div className="stage4-hud-col stage4-hud-col--right">
          <div className="stage4-point-box">
            <h4>Score:</h4> <span>{displayTotalScore}</span>
          </div>
          <button
            type="button"
            className="stage4-timericon stage4-skip"
            disabled={submitting}
            onClick={handleSkip}
          >
            Skip
          </button>
        </div>
      </div>

      <div className="stage4-body">
        <div className="stage4-reference">
          <h3 className="stage4-reference-title">Reference Image</h3>
          <img src={referenceImage} alt="Reference" className="stage4-reference-img" />
        </div>

        <div className="stage4-puzzle-wrap">
          <div className={`stage4-puzzle-grid${solved ? " is-solved" : ""}`}>
            {solved ? (
              <img src={referenceImage} alt="Completed puzzle" className="stage4-full-img" />
            ) : (
              order.map((pieceId, slot) => {
                const isCorrect = pieceId === slot;
                return (
                  <div
                    key={slot}
                    data-slot={slot}
                    className={`stage4-piece${isCorrect ? " is-correct" : ""}${
                      draggingSlot === slot ? " is-dragging" : ""
                    }`}
                    style={{
                      backgroundImage: `url("${referenceImage}")`,
                      backgroundSize: `${GRID_SIZE * 100}% ${GRID_SIZE * 100}%`,
                      backgroundPosition: pieceBgPos(pieceId),
                      touchAction: "none",
                    }}
                    onPointerDown={(e) => handlePointerDown(slot, e)}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    onPointerCancel={handlePointerCancel}
                  />
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
